
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { useBusinessSubscription } from '@/hooks/useSubscription';
import { PATHS } from '@/routes/routePaths';
import { useToast } from '@/hooks/use-toast';
import { useUserBusiness } from '@/hooks/useUserBusiness';

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const businessId = searchParams.get('business_id');
  const sessionId = searchParams.get('session_id');
  const { refetch } = useBusinessSubscription(businessId || undefined);
  const [countdown, setCountdown] = useState(5);
  const { data: userBusiness, isLoading: businessLoading } = useUserBusiness();

  // Check if business is active and redirect if not
  useEffect(() => {
    if (!businessLoading && userBusiness && userBusiness.is_active === false) {
      toast({
        title: "Access Denied",
        description: "Subscription features are only available for active businesses.",
        variant: "destructive"
      });
      navigate(PATHS.ADMIN_MY_BUSINESS);
      return;
    }
  }, [userBusiness, businessLoading, navigate, toast]);

  // Effect to redirect after subscription successful
  useEffect(() => {
    // Refetch subscription status after successful checkout
    if (businessId) {
      refetch();
    }
    
    // Set up automatic redirect with countdown
    const timer = setInterval(() => {
      setCountdown(prevCountdown => {
        if (prevCountdown <= 1) {
          clearInterval(timer);
          navigate(PATHS.ADMIN_MY_BUSINESS);
          return 0;
        }
        return prevCountdown - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [businessId, sessionId, refetch, navigate]);

  const handleGoToBusiness = () => {
    navigate(PATHS.ADMIN_MY_BUSINESS);
  };

  return (
    <div className="container py-12 flex items-center justify-center min-h-[70vh]">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md"
      >
        <Card className="text-center">
          <CardHeader>
            <div className="flex justify-center mb-4">
              <CheckCircle className="h-16 w-16 text-green-500" />
            </div>
            <CardTitle className="text-2xl">Subscription Successful!</CardTitle>
            <CardDescription>
              Your subscription has been processed successfully.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              Thank you for subscribing. Your business now has access to additional features based on your subscription plan.
            </p>
            <div className="flex flex-col items-center gap-2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent mb-2" />
              <p className="text-sm text-muted-foreground">
                Redirecting to your business page in <span className="font-semibold">{countdown}</span> seconds...
              </p>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-2">
            <Button 
              variant="outline" 
              onClick={handleGoToBusiness}
              className="w-full"
            >
              <ArrowRight className="mr-2 h-4 w-4" />
              Go to My Business
            </Button>
          </CardFooter>
        </Card>
      </motion.div>
    </div>
  );
};

export default SubscriptionSuccessPage;
